import { apiService } from "@/services/api-service";
import { getRoleList } from "@/api/role";
import stompClient from "@/services/stomp/stompClient";
import {
  setCookie,
  getCookie,
  removeCookie,
  parseJwt,
  resetAllStores,
} from "@/utils/auth";
import {
  checkNotificationPermission,
  getCurrentFCMToken,
  setFCMToken,
  removeStorageFCMToken,
} from "../../services/firebase/cloud-messaging/firebase-messaging";
import moment from "moment";

let timer = "";

const state = () => {
  return {
    token: getCookie("ezipart-token") || null,
    userId: Number(getCookie("ezipart-userId")) || null,
    userName: localStorage.getItem("ezipart-userName", "") || null,
    usersName: localStorage.getItem("ezipart-usersName", "") || null,
    active: localStorage.getItem("user-active", "") || null,
    postCode: localStorage.getItem("user-postCode", "") || null,
    streetAddress: localStorage.getItem("user-address", "") || null,
    suburb: localStorage.getItem("user-suburb", "") || null,
    permission: localStorage.getItem("user-permission") || [],
    role: localStorage.getItem("user-role") || null,
    avatar: localStorage.getItem("user-avatar") || null,
    fcmToken: getCurrentFCMToken() || null,
    isConnectedToXero: false,
    isLogout: false,
  };
};

const mutations = {
  SET_TOKEN: (state, token) => {
    state.token = token;
  },
  SET_USERNAME: (state, name) => {
    state.userName = name;
  },
  SET_USERSNAME: (state, name) => {
    state.usersName = name;
  },
  SET_USERID: (state, userId) => {
    state.userId = userId;
  },
  SET_USER_ACTIVE: (state, userActive) => {
    state.userInfo = userActive;
  },
  SET_USER_POSTCODE: (state, postCode) => {
    state.userInfo = postCode;
  },
  SET_USER_ADDRESS: (state, streetAddress) => {
    state.userInfo = streetAddress;
  },
  SET_USER_SUBURB: (state, suburb) => {
    state.userInfo = suburb;
  },
  SET_USER_PERMISSION: (state, permission) => {
    state.permission = permission;
  },
  SET_USER_ROLE: (state, role) => {
    state.role = role;
  },
  SET_USER_AVATAR: (state, avatarUrl) => {
    state.avatar = avatarUrl;
  },
  SET_FCM_TOKEN: (state, fcmToken) => {
    setFCMToken(fcmToken);
    state.fcmToken = fcmToken;
  },
  SET_USER_CONNECTED: (state, connected) => {
    state.isConnectedToXero = connected;
  },
  SET_USER_LOGIN: (state) => {
    state.isLogout = false;
  },
  SET_USER_LOGOUT: (state) => {
    state.isLogout = true;
  },
};

const actions = {
  loginHandler({ dispatch, state }, userInfo) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          console.log('[loginHandler] Starting login...');
          const result = await apiService.auth.login(userInfo);

          const { code, data, msg } = result;
          if (code === 200) {
            console.log('[loginHandler] Login successful, setting user info...');
            dispatch("setUserInfoLogin", data);
            await dispatch("getAllRoles");

            console.log('[loginHandler] Login complete, resolving...');

            // IMPORTANT: Resolve promise BEFORE post-login tasks
            // This prevents WebSocket/Notification errors from triggering logout
            resolve({ access: true, msg });

            // Post-login tasks (run in background, errors won't affect login)
            (async () => {
              try {
                console.log('[loginHandler] Connecting WebSocket...');
                await dispatch("stompClient/connectWS", null, { root: true });
                console.log('[loginHandler] WebSocket connected');
              } catch (wsError) {
                console.error('[loginHandler] WebSocket failed (non-critical):', wsError);
              }

              try {
                console.log('[loginHandler] Checking notifications...');
                checkNotificationPermission(Notification.permission, state);
                console.log('[loginHandler] Notifications checked');
              } catch (notifError) {
                console.error('[loginHandler] Notifications failed (non-critical):', notifError);
              }

              try {
                dispatch("setAutoLogout", data.accessToken);
              } catch (autoLogoutError) {
                console.error('[loginHandler] Auto-logout setup failed (non-critical):', autoLogoutError);
              }
            })();

          } else {
            console.error('[loginHandler] Login failed, code:', code);
            reject("Login Fail");
          }
        } catch (err) {
          console.error('[loginHandler] LOGIN ERROR - clearing user info:', err);
          dispatch("clearUserInfo");
          reject(err.message);
        }
      })();
    });
  },

  async getAllRoles() {
    try {
      const res = await getRoleList();
      const { code, data } = res;
      if (code === 200) {
        const roles = data.map((role) => role.roleName);
        setCookie("roles", JSON.stringify(roles));
      }
    } catch (error) {
      throw new Error(error);
    }
  },

  setUserInfo({ commit }, userInfo) {
    commit("SET_USERSNAME", userInfo.usersName);
    commit("SET_USER_ACTIVE", userInfo.active);
    commit("SET_USER_POSTCODE", userInfo.postCode);
    commit("SET_USER_ADDRESS", userInfo.streetAddress);
    commit("SET_USER_SUBURB", userInfo.suburb);
    commit("SET_USER_AVATAR", userInfo.avatar);

    localStorage.setItem("ezipart-usersName", userInfo.usersName);
    localStorage.setItem("user-active", userInfo.active);
    localStorage.setItem("user-postCode", userInfo.postCode);
    localStorage.setItem("user-address", userInfo.streetAddress);
    localStorage.setItem("user-suburb", userInfo.suburb);
    localStorage.setItem("user-avatar", userInfo.avatar ?? "");
  },

  async setUserInfoLogin({ commit, dispatch }, data) {
    commit("SET_USER_LOGIN");
    commit("SET_TOKEN", data.accessToken);
    commit("SET_USERID", data.userInfo.userID);
    commit("SET_USERNAME", data.userInfo.userName);
    commit("SET_USER_ROLE", data.userInfo.role);
    commit("SET_USER_PERMISSION", data.userInfo.permission);
    commit("SET_USER_AVATAR", data.userInfo.avatar);

    setCookie("ezipart-token", data.accessToken);
    setCookie("ezipart-userId", data.userInfo.userID);
    localStorage.setItem("ezipart-userId", data.userInfo.userID);
    localStorage.setItem("ezipart-userName", data.userInfo.userName);
    localStorage.setItem("user-role", data.userInfo.role);
    localStorage.setItem("user-permission", data.userInfo.permission);
    localStorage.setItem("user-avatar", data.userInfo.avatar ?? "");

    await dispatch("setUserInfo", data.userInfo);
  },

  setFCMToken({ commit }, fcmToken) {
    commit("SET_FCM_TOKEN", fcmToken);
  },

  async removeFCMToken({ state, dispatch }) {
    const userId = JSON.parse(localStorage.getItem("ezipart-userId"));
    try {
      await stompClient.send("/app/fcmToken/removeToken", {
        userID: userId,
        fcmToken: state.fcmToken,
      });
    } catch (error) {
      console.log("Reconnecting...");
      try {
        await dispatch(
          "stompClient/reconnectWS",
          async () => {
            await stompClient.send("/app/fcmToken/removeToken", {
              userID: userId,
              fcmToken: state.fcmToken,
            });
            console.log("remove fcm token success");
          },
          { root: true }
        );
      } catch (error) {
        console.log("Error removing FCM Token");
      }
    }
  },

  setAutoLogout({ dispatch }, accessToken) {
    // token (auto logout)
    const currentTime = moment();
    const token = accessToken.split(" ")[1];
    const decodedToken = parseJwt(token);
    const expirationTime = moment
      .unix(decodedToken.exp)
      .diff(currentTime, "milliseconds");
    timer = setTimeout(() => {
      dispatch("logoutHandler");
    }, expirationTime);
  },

  clearUserInfo({ commit, dispatch }) {
    commit("SET_FCM_TOKEN", null);
    removeStorageFCMToken();
    dispatch("lastVisited/clearAll", null, { root: true });
    localStorage.removeItem("user-permission");
    localStorage.removeItem("user-role");
    localStorage.removeItem("tabs-va");
    localStorage.removeItem("tabs-vehicle");
    localStorage.removeItem("tabs-invoice");
    localStorage.removeItem("tabs-po");
    localStorage.removeItem("ezipart-userName");
    localStorage.removeItem("ezipart-usersName");
    localStorage.removeItem("ezipart-userId");
    localStorage.removeItem("user-active");
    localStorage.removeItem("user-address");
    localStorage.removeItem("user-postCode");
    localStorage.removeItem("user-suburb");
    localStorage.removeItem("roles");
    localStorage.removeItem("user-avatar");

    // Clear sessionStorage for inventory cache
    sessionStorage.removeItem("ezipart-inventory-cache");
    sessionStorage.removeItem("ezipart-inventory-clearOnReturn");

    removeCookie("ezipart-token");
    removeCookie("ezipart-userId");
  },

  async logoutHandler({ commit, dispatch }) {
    await dispatch("removeFCMToken");
    dispatch("stompClient/disconnectWS", null, { root: true });

    dispatch("clearUserInfo");

    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    resetAllStores(this);
    commit("SET_USER_LOGOUT");
  },

  async checkConnectionToXero({ commit }) {
    try {
      const result = await apiService.setting.checkConnectedXeroAccount();
      if (result.code === 200) {
        commit("SET_USER_CONNECTED", result.data);
      }
    } catch (e) {
      console.log(e);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
